import React, { useState, useRef, useEffect } from "react";
import {
  ExclamationCircleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { verify2FA } from "../../crud/auth.crud";

/**
 * Code-entry screen for users who already have an authenticator enrolled.
 * Exchanges a 6-digit TOTP code (plus the short-lived MFA-pending token) for a
 * real session via the backend /auth/2fa/verify endpoint.
 */
export default function TwoFactorVerify({ tempToken, onAuthenticated, onCancel }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current && inputRef.current.focus();
  }, []);

  const handleCodeChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    if (error) setError("");
  };

  const submit = (e) => {
    e.preventDefault();
    if (code.length !== 6 || loading) return;
    setLoading(true);
    setError("");

    verify2FA(tempToken, code)
      .then(({ data }) => {
        setLoading(false);
        if (data && data.errors) {
          setCode("");
          setError(data.message || "Invalid code. Please try again.");
          return;
        }
        onAuthenticated(data.result);
      })
      .catch((err) => {
        setLoading(false);
        setCode("");
        const status = err && err.response && err.response.status;
        if (status === 429) {
          setError("Too many attempts. Please wait a few minutes and try again.");
        } else if (status === 401 || status === 400) {
          const msg = err.response.data && err.response.data.message;
          setError(msg || "Invalid code. Please try again.");
        } else {
          setError("Something went wrong. Please try again.");
        }
        inputRef.current && inputRef.current.focus();
      });
  };

  return (
    <form onSubmit={submit} className="tw-space-y-6">
      <div className="tw-text-center">
        <div className="tw-mx-auto tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-100 dark:tw-bg-blue-900/40">
          <ShieldCheckIcon className="tw-h-6 tw-w-6 tw-text-blue-600 dark:tw-text-blue-400" />
        </div>
        <h2 className="tw-mt-4 tw-text-lg tw-font-semibold tw-text-gray-900 dark:tw-text-white">
          Two-Factor Authentication
        </h2>
        <p className="tw-mt-1 tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
          Enter the 6-digit code from your authenticator app.
        </p>
      </div>

      {error && (
        <div className="tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-800 tw-rounded-lg tw-p-4 tw-flex tw-items-start tw-gap-3">
          <ExclamationCircleIcon className="tw-h-5 tw-w-5 tw-text-red-600 dark:tw-text-red-400 tw-flex-shrink-0 tw-mt-0.5" />
          <p className="tw-text-sm tw-text-red-700 dark:tw-text-red-400">{error}</p>
        </div>
      )}

      <div>
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          name="code"
          value={code}
          onChange={handleCodeChange}
          placeholder="000000"
          className="tw-block tw-w-full tw-text-center tw-tracking-[0.5em] tw-text-2xl tw-font-semibold tw-py-3 tw-border tw-rounded-lg tw-bg-white dark:tw-bg-gray-700 tw-text-gray-900 dark:tw-text-white placeholder:tw-text-gray-300 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-border-gray-300 dark:tw-border-gray-600"
        />
      </div>

      <button
        type="submit"
        disabled={loading || code.length !== 6}
        className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-3 tw-rounded-lg tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-font-semibold tw-shadow-lg hover:tw-shadow-xl tw-transition-all disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
      >
        {loading ? "Verifying..." : "Verify"}
      </button>

      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="tw-w-full tw-text-sm tw-font-medium tw-text-gray-500 hover:tw-text-gray-700 dark:tw-text-gray-400 dark:hover:tw-text-gray-200 tw-transition-colors"
        >
          Back to sign in
        </button>
      )}
    </form>
  );
}
